import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useEffect } from "react";
import { loadGeoHint } from "@/lib/geo-hint";
import { api, type SearchParams, type CarCardData, type SearchResult } from "@/lib/api";
import { getAlgoliaFacetCounts, isAlgoliaConfigured } from "@/lib/algolia";
import { shouldRetryCarDetail } from "@/lib/car-detail-error";
import { readPublicSsrQuerySeed } from "@/lib/ssr-bootstrap";
import { useRegionalSearchSeed } from "@/lib/regional-search";
import {
  loadCategoryBrowserCars,
  type CategoryBrowserResult,
} from "@/lib/category-browser-cars";
import { selectNearbyAlternatives } from "@/lib/homepage-rail-selection";
import { carsInCountry, normalizeInventoryCountry } from "@/lib/country-inventory";
import {
  canRecoverNearbySearch,
  getNearbyRecoveryRadiusSteps,
  NEARBY_RECOVERY_MIN_RESULTS,
  type NearbyRecoveryEligibility,
  type SearchResultMeta,
} from "@/lib/search-location-intent";
import {
  compareWeddingCars,
  isWeddingSuitableCar,
} from "@workspace/booking-rules";

export function useGeoLocation() {
  return useQuery({
    queryKey: ["geo-location"],
    queryFn: api.geoLookup,
    staleTime: 1000 * 60 * 60,
    retry: false,
  });
}

export interface GeoHint {
  country: string | null;
  language: string | null;
}

// Server-side hint: country is derived from CDN headers (CF-IPCountry,
// x-vercel-ip-country) when available, otherwise from a local MaxMind
// lookup against the request IP. Returns { country: null, language: null }
// for private/loopback IPs (e.g. local dev). Cached for one hour because
// IP-derived geography essentially never changes for a given visitor.
export function useGeoHint(opts?: { enabled?: boolean }) {
  return useQuery<GeoHint>({
    queryKey: ["geo-hint"],
    queryFn: loadGeoHint,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
    retry: false,
    enabled: opts?.enabled ?? true,
  });
}

// Progressive-radius nearby cars: starts at ~1.5° (≈165km) and widens
// out to country-scale until at least `minResults` cars are returned, so
// hosts in low-density regions (e.g. Isle of Man) still see a populated
// "Cars near {city}" carousel instead of a single lonely listing.
// Returns the last attempt's result if even the widest radius can't
// fill the carousel.
export function useNearbyCars(
  lat: number | null | undefined,
  lng: number | null | undefined,
  limit = 6,
  minResults = limit,
  excludedIds: ReadonlySet<number> = new Set(),
  countryCode?: string,
) {
  const country = normalizeInventoryCountry(countryCode);
  const radii = [1.5, 3, 6, 12, 25];
  // Fetch enough candidates to survive the cross-rail exclusion. The API
  // caps a page at 48, so a larger page gives the nearby rail room to replace
  // Trending cars without adding another request at the same radius.
  const candidatePageSize = Math.min(
    48,
    Math.max(limit * 3, limit + excludedIds.size),
  );
  const excludedIdKey = [...excludedIds].sort((a, b) => a - b);

  return useQuery({
    queryKey: ["nearby-cars", lat, lng, limit, minResults, excludedIdKey, country ?? ""],
    queryFn: async () => {
      if (lat == null || lng == null) {
        return api.searchCars({ page: 1, pageSize: 0 });
      }
      let last: Awaited<ReturnType<typeof api.searchCars>> | null = null;
      for (const radius of radii) {
        const result = await api.searchCars({
          country_code: country,
          bounds_north: lat + radius,
          bounds_south: lat - radius,
          bounds_east: lng + radius,
          bounds_west: lng - radius,
          page: 1,
          pageSize: candidatePageSize,
          sort: "score",
        });
        const alternatives = selectNearbyAlternatives(carsInCountry(result.cars ?? [], country), excludedIds);
        last = {
          ...result,
          cars: alternatives.slice(0, limit),
          pageSize: limit,
        };
        if (alternatives.length >= minResults) return last;
      }
      if (country) {
        const result = await api.searchCars({ country_code: country, page: 1, pageSize: candidatePageSize, sort: "score" });
        return {
          ...result,
          cars: selectNearbyAlternatives(carsInCountry(result.cars ?? [], country), excludedIds).slice(0, limit),
          pageSize: limit,
        };
      }
      return last ?? api.searchCars({ page: 1, pageSize: 0 });
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 5,
  });
}

// Single staged-waterfall query powering the home "Find your next drive"
// category rail. Replaces the five parallel /cars/search calls the section
// used to fire on every page load with one query that broadens in stages and
// short-circuits the moment it has 8 classics:
//   stage 1  city (±1.5°) + region (±5°) around the visitor — geo only
//   stage 2  country (+ the global car_type pool when a category tab is
//            active) — still filtered to the chosen car_type
//   stage 3  any car in-country, then the global featured set — filter dropped
// Dense markets settle in 1-2 requests and the rail reveals in one transition
// instead of popping in tier-by-tier. The year≤1995 classic filter and the
// cross-tier dedupe run inside the queryFn so "is the rail full yet?" is
// judged on the cars actually shown. `countAfterCity`/`countAfterCategory`
// are returned so the caller can still surface the "widened beyond {category}"
// note.
export function useCategoryBrowserCars(
  activeSlug: string,
  lat: number | null | undefined,
  lng: number | null | undefined,
  countryCode: string | undefined,
  options?: { enabled?: boolean; initialData?: CategoryBrowserResult; allowWorldwideFallback?: boolean; fallbackMarkets?: readonly string[] },
) {
  return useQuery<CategoryBrowserResult>({
    queryKey: ["cb-waterfall", activeSlug, lat, lng, countryCode ?? "", options?.allowWorldwideFallback ?? false, options?.fallbackMarkets?.join(",") ?? ""],
    enabled: options?.enabled ?? true,
    // Country-root documents seed the primary rail from the frozen cohort.
    initialData: options?.initialData,
    initialDataUpdatedAt: options?.initialData ? Date.now() : undefined,
    staleTime: 1000 * 60 * 5,
    placeholderData: previous => previous && (
      !countryCode || carsInCountry(previous.cars, countryCode).length === previous.cars.length
    ) ? previous : undefined,
    // Bound outage traffic. A failed rail must not poll the same catalogue
    // server indefinitely while visitors are already receiving 503s.
    retry: 1,
    retryDelay: 750,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => loadCategoryBrowserCars(
      activeSlug,
      lat,
      lng,
      countryCode,
      api.searchCars,
      api.getFeaturedCars,
      { allowWorldwideFallback: options?.allowWorldwideFallback, fallbackMarkets: options?.fallbackMarkets },
    ),
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: api.getStats,
    staleTime: 1000 * 60 * 10,
  });
}

export function useFeaturedCars(limit = 6, country?: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["featured-cars", limit, country],
    queryFn: () => api.getFeaturedCars(limit, country || undefined),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

export function useCarTypes() {
  return useQuery({
    queryKey: ["car-types"],
    queryFn: api.getCarTypes,
    staleTime: 1000 * 60 * 30,
  });
}

export function useDestinations(limit = 8, countryCode?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["destinations", limit, countryCode],
    queryFn: () => api.getDestinations(limit, countryCode),
    staleTime: 1000 * 60 * 15,
    enabled: options?.enabled ?? true,
  });
}

export function useMakeInventoryCounts(enabled = true) {
  return useQuery({
    queryKey: ["make-inventory-counts", isAlgoliaConfigured ? "algolia" : "supabase"],
    enabled,
    queryFn: async () => {
      if (isAlgoliaConfigured) {
        try {
          const makeCounts = await getAlgoliaFacetCounts("make");
          if (makeCounts.length > 0) {
            return {
              makes: makeCounts.map(({ value, count }) => ({ make: value, count })),
            };
          }
        } catch {
          // fall through to Supabase
        }
      }
      // The Supabase search response contains a result page, not aggregate
      // per-make facets. Use the dedicated all-active-listings count endpoint
      // rather than deriving popularity from the current page.
      const { makeCounts } = await api.getMakeCounts();
      return { makes: makeCounts };
    },
    staleTime: 1000 * 60 * 30,
  });
}

export function useMakes(enabled = true) {
  const query = useMakeInventoryCounts(enabled);
  return {
    ...query,
    data: query.data ? { makes: query.data.makes.map((item) => item.make) } : undefined,
  };
}

export interface SearchCarsRecoveryOptions extends NearbyRecoveryEligibility {
  requestedRadiusMiles?: number;
}

export function applyWeddingSearchPolicy(
  result: SearchResult,
  rentalType: string | undefined,
): SearchResult {
  if (rentalType !== "wedding") return result;
  const cars = result.cars
    .filter((car) => isWeddingSuitableCar({
      year: car.year,
      make: car.make,
      rentalEventActive: car.rentalEventActive,
      rentalChauffeuredActive: car.rentalChauffeurActive,
      priceEvent: car.priceEvent,
      eventMinPrice: car.eventMinPrice,
      eventHourlyRate: car.eventHourlyRate,
      chauffeuredMinPrice: car.chauffeurMinPrice,
      chauffeuredHourlyRate: car.chauffeuredHourlyRate,
      minimumPrice: car.minimumPrice,
    }))
    .sort(compareWeddingCars);
  return { ...result, cars };
}

export async function searchCarsWithNearbyRecovery(
  params: SearchParams,
  recovery: SearchCarsRecoveryOptions | undefined,
  readSearch: typeof api.searchCars = api.searchCars,
): Promise<SearchResult> {
  const exact = applyWeddingSearchPolicy(await readSearch(params), params.rental_type);
  const requestedRadiusMiles = recovery?.requestedRadiusMiles ?? params.radius;
  const exactMeta: SearchResultMeta = {
    scope: "exact",
    requestedRadiusMiles,
    appliedRadiusMiles: requestedRadiusMiles,
    exactResultsCount: exact.total,
  };

  // A failed request throws above; only a successful zero-result exact query
  // can start recovery. This preserves existing error behavior.
  if (exact.total !== 0 || !recovery || !canRecoverNearbySearch(recovery)) {
    return { ...exact, searchMeta: exactMeta };
  }

  const steps = getNearbyRecoveryRadiusSteps(requestedRadiusMiles);
  if (steps.length === 0) {
    return { ...exact, searchMeta: { ...exactMeta, recoveryAttempted: true } };
  }

  let firstNonEmpty: { result: SearchResult; radius: number } | null = null;
  for (const [index, radius] of steps.entries()) {
    const candidate = applyWeddingSearchPolicy(
      await readSearch({ ...params, radius }),
      params.rental_type,
    );
    if (candidate.cars.length > 0 && firstNonEmpty === null) {
      firstNonEmpty = { result: candidate, radius };
    }
    if (candidate.cars.length >= NEARBY_RECOVERY_MIN_RESULTS) {
      return {
        ...candidate,
        searchMeta: {
          scope: "nearby",
          requestedRadiusMiles,
          appliedRadiusMiles: radius,
          exactResultsCount: 0,
          recoveryAttempted: true,
        },
      };
    }
    if (index === steps.length - 1 && firstNonEmpty) {
      return {
        ...firstNonEmpty.result,
        searchMeta: {
          scope: "nearby",
          requestedRadiusMiles,
          appliedRadiusMiles: firstNonEmpty.radius,
          exactResultsCount: 0,
          recoveryAttempted: true,
        },
      };
    }
  }

  return { ...exact, searchMeta: { ...exactMeta, recoveryAttempted: true } };
}

export function useSearchCars(params: SearchParams, recovery?: SearchCarsRecoveryOptions, enabled = true) {
  const seed = useRegionalSearchSeed();
  const seeded = seed && (seed.params ? JSON.stringify(params) === JSON.stringify(seed.params) : params.country_code === seed.countryCode &&
    Object.entries(params).every(([key, value]) => value == null || value === "" ||
      key === "country_code" || (key === "page" && value === 1) ||
      (key === "pageSize" && value === 24) || (key === "sort" && value === "relevance")));
  const query = useQuery({
    queryKey: ["search-cars", params, recovery],
    queryFn: () => searchCarsWithNearbyRecovery(params, recovery),
    staleTime: 1000 * 60 * 2,
    retry: 1,
    retryDelay: 750,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Keep the last completed results visible while a changed search intent
    // is fetching. SearchPage marks these cards and map pins as updating.
    placeholderData: keepPreviousData,
    initialData: seeded ? seed.searchResult : undefined,
    enabled: enabled && !(seeded && seed.frozen),
  });
  // initialData cannot override an older populated query cache. The active
  // bootstrap owns the first frame; ordinary finite-stale queries resume later.
  return seeded && seed.frozen ? { ...query, data: seed.searchResult,
    isLoading: false, isPending: false, isFetching: false, isError: false, error: null } : query;
}

export function shouldEnableCarDetailQuery(
  id: number | string,
  slug: string,
  opts?: { preview?: boolean; previewAuthLoading?: boolean; previewAccessToken?: string; idOnly?: boolean },
): boolean {
  return !!id && (!!slug || opts?.idOnly === true)
    && (!opts?.preview || !!opts.previewAccessToken);
}

export function useCarDetail(
  id: number | string,
  slug: string,
  opts?: {
    preview?: boolean;
    previewAccessToken?: string;
    previewAuthLoading?: boolean;
    dateFrom?: string;
    dateTo?: string;
    rentalType?: "driving" | "chauffeured" | "event";
    idOnly?: boolean;
    publicArchive?: string;
    completeRelated?: boolean;
  },
) {
  const queryKey = [
    "car-detail", id, slug, opts?.preview, opts?.dateFrom, opts?.dateTo, opts?.rentalType, opts?.idOnly,
    ...(opts?.publicArchive ? [opts.publicArchive] : []),
  ];
  const seed = readPublicSsrQuerySeed<Awaited<ReturnType<typeof api.getCarDetail>>>(queryKey);
  const query = useQuery({
    queryKey,
    initialData: seed?.data,
    initialDataUpdatedAt: seed?.updatedAt,
    queryFn: () => api.getCarDetail(id, slug, opts),
    // Never issue an anonymous draft request. AuthProvider may still be
    // loading the profile after providing a valid session token.
    enabled: shouldEnableCarDetailQuery(id, slug, opts),
    // Task #6672 — bounded automatic retry for TRANSIENT failures only
    // (network throw / 5xx / timeout). An authoritative 404 (listing
    // confirmed gone) and the slug-redirect sentinel never retry.
    retry: (failureCount, error) => shouldRetryCarDetail(failureCount, error),
  });
  useEffect(() => {
    if (query.error && seed?.data) console.warn("[public-ssr] Car refresh failed; keeping server content", { id, slug });
  }, [query.error, seed?.data, id, slug]);
  return { ...query, data: query.data ?? seed?.data };
}

export function useCarReviews(id: number | string, slug: string, page: number) {
  return useQuery({
    queryKey: ["car-reviews", String(id), slug, page],
    queryFn: () => api.getCarReviews(id, slug, page),
    enabled: !!id && !!slug,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCarAvailability(id: number | string, slug: string, enabled = true) {
  const queryKey = ["car-availability", id, slug];
  const seed = enabled ? readPublicSsrQuerySeed<Awaited<ReturnType<typeof api.getCarAvailability>>>(queryKey) : undefined;
  return useQuery({
    queryKey,
    initialData: seed?.data,
    initialDataUpdatedAt: seed?.updatedAt,
    queryFn: () => api.getCarAvailability(id, slug),
    enabled: enabled && !!id && !!slug,
  });
}

export function useInsurancePackages() {
  return useQuery({
    queryKey: ["insurance-packages"],
    queryFn: api.getInsurancePackages,
    staleTime: 1000 * 60 * 30,
  });
}
